import { createFileRoute } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { LegalLayout, InfoBlock, type LegalSection } from "@/components/calv/LegalLayout";

const sections: LegalSection[] = [
  {
    id: "overview",
    num: "01",
    title: "Overview",
    content: (
      <>
        <p>
          The protection of your personal data is important to us. We treat your personal data
          confidentially and in accordance with legal data protection regulations and this
          privacy policy.
        </p>
        <p>
          The following notes provide a simple overview of what happens to your personal data
          when you visit this website.
        </p>
      </>
    ),
  },
  {
    id: "responsible",
    num: "02",
    title: "Responsible Entity",
    content: (
      <>
        <p>Responsible for data processing on this website is:</p>
        <InfoBlock>
          calvarius GmbH<br />
          Kalvarienberg 20<br />
          97753 Karlstadt<br />
          Germany<br />
          Phone: +49 9353 9768 000<br />
          Email: <a href="mailto:info@calvarius.com">info@calvarius.com</a>
        </InfoBlock>
      </>
    ),
  },
  {
    id: "general",
    num: "03",
    title: "General Information",
    content: (
      <p>
        Personal data is any data that can be used to personally identify you. Detailed
        information on data protection can be found in the following privacy policy.
      </p>
    ),
  },
  {
    id: "logs",
    num: "04",
    title: "Server Log Files",
    content: (
      <>
        <p>
          The provider of these pages automatically collects and stores information in so-called
          server log files, which your browser automatically transmits to us. This includes in
          particular:
        </p>
        <ul>
          <li>Browser type and version</li>
          <li>Operating system used</li>
          <li>Referrer URL</li>
          <li>Hostname of the accessing computer</li>
          <li>Time of server request</li>
          <li>IP address</li>
        </ul>
        <p>This data is not merged with other data sources.</p>
      </>
    ),
  },
  {
    id: "contact",
    num: "05",
    title: "Contact",
    content: (
      <>
        <p>
          If you send us inquiries via contact form, email or phone, your information including
          the contact data you provided will be stored for the purpose of processing the inquiry
          and for follow-up questions.
        </p>
        <p>We will not share this data without your consent.</p>
      </>
    ),
  },
  {
    id: "analytics",
    num: "06",
    title: "Analytics Tools and Tracking",
    content: (
      <>
        <p>
          For the purpose of demand-oriented design and continuous optimization of our pages, we
          use analytics and tracking tools. We currently use <strong>PostHog</strong> (provider:
          PostHog Inc., 2261 Market Street #4008, San Francisco, CA 94114, USA; data hosted in
          the EU). Personal data may be processed, including:
        </p>
        <ul>
          <li>Usage data</li>
          <li>Device information</li>
          <li>Interaction data</li>
        </ul>
        <p>Processing is based on your consent according to Art. 6 (1) lit. a GDPR.</p>

        <h3 id="posthog" className="scroll-mt-24">
          Web analytics with PostHog
        </h3>
        <p>
          <strong>Provider:</strong> PostHog Inc., 2261 Market Street #4008, San Francisco, CA
          94114, USA. Our data is hosted exclusively on servers located in the European Union
          (Frankfurt, AWS).
        </p>
        <p>
          <strong>Purpose:</strong> We use PostHog to understand how visitors use our website,
          which content is in demand, and how we can improve the offering. The data collected
          includes pages viewed, clicks on UI elements, time spent on page, approximate
          geographic region (city/region level), browser type, operating system, screen size,
          and referrer.
        </p>
        <p>
          <strong>Legal basis:</strong> Art. 6 (1) (a) GDPR (consent). Processing only takes
          place if you have consented via our cookie banner in the "Statistics / Analytics"
          category.
        </p>
        <p>
          <strong>Storage location:</strong> All data is processed and stored in the EU
          (Frankfurt, AWS). PostHog Inc. has access for maintenance and support purposes only;
          a Data Processing Agreement pursuant to Art. 28 GDPR is in place.
        </p>
        <p>
          <strong>Retention period:</strong> 12 months, after which data is automatically
          deleted or aggregated.
        </p>
        <p>
          <strong>Session replays:</strong> Screen recordings are created. Input fields
          (passwords, email, form content) are masked by default and not recorded.
        </p>
        <p>
          <strong>Withdrawal:</strong> You can withdraw your consent at any time via the cookie
          settings (link in the footer).
        </p>
        <p>
          <strong>Provider's privacy policy:</strong>{" "}
          <a href="https://posthog.com/privacy">https://posthog.com/privacy</a>
        </p>
      </>
    ),
  },
  {
    id: "cookies",
    num: "07",
    title: "Cookies",
    content: (
      <>
        <p>
          Our website uses cookies. Cookies are small text files that are stored on your device
          and that your browser saves.
        </p>
        <p>They serve to make our offer more user-friendly, effective, and secure.</p>
      </>
    ),
  },
  {
    id: "rights",
    num: "08",
    title: "Your Rights",
    content: (
      <>
        <p>You have the right at any time to:</p>
        <ul>
          <li>Information about your stored personal data</li>
          <li>Correction or deletion of this data</li>
          <li>Restriction of processing</li>
          <li>Objection to processing</li>
          <li>Data portability</li>
        </ul>
        <p>For this and other questions on data protection, you can contact us at any time.</p>
      </>
    ),
  },
  {
    id: "revocation",
    num: "09",
    title: "Revocation of Your Consent",
    content: (
      <p>
        Many data processing operations are only possible with your express consent. You can
        revoke any consent you have given at any time.
      </p>
    ),
  },
  {
    id: "ssl",
    num: "10",
    title: "SSL/TLS Encryption",
    content: (
      <p>
        For security reasons and to protect the transmission of confidential content, this site
        uses SSL or TLS encryption.
      </p>
    ),
  },
  {
    id: "changes",
    num: "11",
    title: "Changes to This Privacy Policy",
    content: (
      <p>
        We reserve the right to adapt this privacy policy to ensure it always complies with
        current legal requirements or to implement changes to our services.
      </p>
    ),
  },
  {
    id: "lighthouse-tool",
    num: "12",
    title: "Lighthouse Live Check Tool",
    content: (
      <>
        <p>
          Our Lighthouse live check at <code>/en/tools/lighthouse-check</code> processes the
          following data:
        </p>
        <ul>
          <li>The website URL you enter</li>
          <li>
            The audit results returned by Google PageSpeed Insights (Lighthouse scores and
            Core Web Vitals)
          </li>
          <li>The timestamp of the audit</li>
        </ul>
        <p>
          This data is stored in our database and used for internal analysis of tool usage.
          We do <strong>not</strong> collect IP addresses, <strong>no</strong> user-agent
          strings, <strong>no</strong> browser fingerprints, and <strong>no</strong> other
          personal data.
        </p>
        <p>
          The tested URL is additionally transmitted to the Google PageSpeed Insights API.
          Their privacy policy is available at{" "}
          <a href="https://policies.google.com/privacy">policies.google.com/privacy</a>.
        </p>
        <p>
          Legal basis: legitimate interest (Art. 6 (1) lit. f GDPR) for improving our service
          and identifying qualified prospects.
        </p>
      </>
    ),
  },
];

export const Route = createFileRoute("/en/privacy-policy")({
  head: () => {
    const h = pageHead("privacy", "en");
    return { meta: h.meta, links: h.links };
  },
  component: () => (
    <LegalLayout
      eyebrow="DP."
      title="Privacy Policy"
      meta="According to GDPR"
      crumbLabel="Privacy"
      sections={sections}
      homeHref="/en"
      homeLabel="Home"
      tocLabel="Contents"
    />
  ),
});
